import { useState, useRef } from "react";
import { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Upload,
  Search,
  Trash2,
  Edit,
  FileSpreadsheet,
  Calendar,
  Download,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useStore } from "@/store/useStore";

const SPORTS = ["Football", "Basketball", "Tennis", "Cricket", "Rugby", "Hockey", "Volleyball", "Baseball", "Gaelic Football", "Hurling", "Camogie"];

export function MatchesPage() {
  const { teams, matches, leagues, addMatch, addMatches, updateMatch, deleteMatch, deleteMatches } = useStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    team1Id: "",
    team1Name: "",
    team2Id: "",
    team2Name: "",
    leagueId: "",
    sport: "",
    date: "",
    time: "",
    venue: "",
    competition: "",
  });
  const fileInputRef = useRef(null);

  // ----- CSV import (with preview + fuzzy team/league name matching) -----
  const [csvFileName, setCsvFileName] = useState("");
  const [csvRows, setCsvRows] = useState([]); // [{home, away, sport, date, time, venue, competition, home_status, away_status, league_status}]
  const [csvImporting, setCsvImporting] = useState(false);

  const filteredMatches = matches.filter(
    (match) =>
      match.team1Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      match.team2Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      match.competition?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMatchIds(filteredMatches.map((m) => m.id));
    } else {
      setSelectedMatchIds([]);
    }
  };

  const handleSelectMatch = (matchId, checked) => {
    if (checked) {
      setSelectedMatchIds((prev) => [...prev, matchId]);
    } else {
      setSelectedMatchIds((prev) => prev.filter((id) => id !== matchId));
    }
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedMatchIds];
    try {
      await deleteMatches(ids);
      toast.success(`Deleted ${ids.length} matches`);
      setSelectedMatchIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete matches");
    }
  };

  const isAllSelected = filteredMatches.length > 0 && selectedMatchIds.length === filteredMatches.length;
  const isSomeSelected = selectedMatchIds.length > 0 && selectedMatchIds.length < filteredMatches.length;

  // ---------- Robust CSV parser (quoted fields + fuzzy team/league matching) ----------

  const parseCsvText = (text) => {
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (c === '"') inQuotes = false;
        else field += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ",") { cur.push(field); field = ""; }
        else if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
        else if (c === "\r") { /* skip */ }
        else field += c;
      }
    }
    if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
    return rows.filter((r) => r.some((v) => String(v).trim().length > 0));
  };

  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\b(fc|sc|cf|f\.c\.|s\.c\.)\b/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const findTeam = (rawName) => {
    if (!rawName) return null;
    const norm = normalize(rawName);
    if (!norm) return null;
    // Exact (case-insensitive) match first
    let hit = teams.find((t) => t.name.toLowerCase() === rawName.toLowerCase());
    if (hit) return hit;
    // Normalized exact match
    hit = teams.find((t) => normalize(t.name) === norm);
    if (hit) return hit;
    // Substring match — pick the LONGEST candidate name to avoid false positives
    const candidates = teams.filter(
      (t) => normalize(t.name).includes(norm) || norm.includes(normalize(t.name)),
    );
    if (candidates.length > 0) {
      candidates.sort((a, b) => b.name.length - a.name.length);
      return candidates[0];
    }
    return null;
  };

  const findLeague = (rawName) => {
    if (!rawName) return null;
    let hit = leagues.find((l) => l.name.toLowerCase() === rawName.toLowerCase());
    if (hit) return hit;
    const norm = normalize(rawName);
    if (!norm) return null;
    hit = leagues.find((l) => normalize(l.name) === norm);
    if (hit) return hit;
    hit = leagues.find((l) => normalize(l.name).includes(norm) || norm.includes(normalize(l.name)));
    return hit || null;
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const text = await file.text();
    const rows = parseCsvText(text);
    if (rows.length === 0) {
      toast.error("CSV is empty");
      return;
    }

    // Header detection — flexible aliases
    const header = rows[0].map((h) => String(h || "").trim().toLowerCase());
    const findIdx = (...aliases) => {
      for (const a of aliases) {
        const i = header.indexOf(a);
        if (i >= 0) return i;
      }
      return -1;
    };
    const idx = {
      home: findIdx("home", "team 1", "team1", "home_team"),
      away: findIdx("away", "team 2", "team2", "away_team"),
      sport: findIdx("sport"),
      date: findIdx("date"),
      time: findIdx("time", "kickoff", "start_time"),
      venue: findIdx("venue", "location", "stadium"),
      competition: findIdx("competition", "league", "league_name", "tournament"),
    };
    const hasHeader = idx.home >= 0 && idx.away >= 0;
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const parsed = dataRows.map((r) => {
      const get = (i) => (i >= 0 && i < r.length ? String(r[i] || "").trim() : "");
      const home = hasHeader ? get(idx.home) : (r[0] || "").trim();
      const away = hasHeader ? get(idx.away) : (r[1] || "").trim();
      const sport = hasHeader ? get(idx.sport) : (r[2] || "").trim();
      const date = hasHeader ? get(idx.date) : (r[3] || "").trim();
      const time = hasHeader ? get(idx.time) : (r[4] || "").trim();
      const venue = hasHeader ? get(idx.venue) : (r[5] || "").trim();
      const competition = hasHeader ? get(idx.competition) : (r[6] || "").trim();

      const team1 = findTeam(home);
      const team2 = findTeam(away);
      const league = findLeague(competition);

      const homeStatus = !home ? "missing" : team1 ? "matched" : "unmatched";
      const awayStatus = !away ? "missing" : team2 ? "matched" : "unmatched";
      const leagueStatus = !competition ? "none" : league ? "matched" : "unmatched";

      return {
        home, away, sport, date, time, venue, competition,
        team1, team2, league,
        homeStatus, awayStatus, leagueStatus,
      };
    });
    setCsvRows(parsed);

    const matchedRows = parsed.filter((p) => p.homeStatus === "matched" && p.awayStatus === "matched").length;
    if (matchedRows < parsed.length) {
      toast.warning(`${matchedRows}/${parsed.length} rows fully matched — review the preview`);
    } else {
      toast.success(`${parsed.length} matches ready to import`);
    }
  };

  const handleConfirmImport = async () => {
    const usable = csvRows.filter(
      (r) => r.home && r.away && (r.homeStatus !== "missing") && (r.awayStatus !== "missing"),
    );
    if (usable.length === 0) {
      toast.error("No usable rows — every row needs a Home and Away team name.");
      return;
    }
    setCsvImporting(true);
    try {
      const newMatches = usable.map((r) => ({
        team1Id: r.team1?.id || null,
        team1Name: r.home,
        team2Id: r.team2?.id || null,
        team2Name: r.away,
        leagueId: r.league?.id || null,
        sport: r.sport || (r.team1?.sport) || (r.team2?.sport) || "",
        date: r.date,
        time: r.time,
        venue: r.venue,
        competition: r.competition || r.league?.name || "",
      }));
      await addMatches(newMatches);
      toast.success(`Imported ${newMatches.length} match${newMatches.length === 1 ? "" : "es"}`);
      const unmatched = csvRows.length - usable.length;
      if (unmatched > 0) toast.warning(`${unmatched} row${unmatched === 1 ? "" : "s"} skipped (missing team name)`);
      setIsCsvDialogOpen(false);
      setCsvRows([]);
      setCsvFileName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setCsvImporting(false);
    }
  };

  const resetCsv = () => {
    setCsvRows([]);
    setCsvFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.team1Name || !formData.team2Name) {
      toast.error("Please fill in team names");
      return;
    }

    const team1 = teams.find((t) => t.name.toLowerCase() === formData.team1Name.toLowerCase());
    const team2 = teams.find((t) => t.name.toLowerCase() === formData.team2Name.toLowerCase());

    const matchData = {
      ...formData,
      team1Id: formData.team1Id || team1?.id || null,
      team2Id: formData.team2Id || team2?.id || null,
    };

    setSubmitting(true);
    try {
      if (editingMatch) {
        await updateMatch(editingMatch.id, matchData);
        toast.success("Match updated successfully");
      } else {
        await addMatch(matchData);
        toast.success("Match added successfully");
      }
      resetForm();
      setIsDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save match");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      team1Id: "",
      team1Name: "",
      team2Id: "",
      team2Name: "",
      leagueId: "",
      sport: "",
      date: "",
      time: "",
      venue: "",
      competition: "",
    });
    setEditingMatch(null);
  };

  const handleEdit = (match) => {
    setEditingMatch(match);
    setFormData({
      team1Id: match.team1Id || "",
      team1Name: match.team1Name || "",
      team2Id: match.team2Id || "",
      team2Name: match.team2Name || "",
      leagueId: match.leagueId || "",
      sport: match.sport || "",
      date: match.date || "",
      time: match.time || "",
      venue: match.venue || "",
      competition: match.competition || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (matchId) => {
    try {
      await deleteMatch(matchId);
      toast.success("Match deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete match");
    }
  };

  const downloadSampleCSV = () => {
    const csv = `home,away,date,time,venue,competition
Manchester United,Liverpool FC,2026-03-15,15:00,Old Trafford,Premier League
TBD,TBD,2026-03-20,18:00,Wembley Stadium,FA Cup Final`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_matches.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const openNewMatchDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Match Schedule
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage upcoming matches and import schedules via CSV
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isCsvDialogOpen} onOpenChange={(open) => { setIsCsvDialogOpen(open); if (!open) resetCsv(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="match-csv-trigger">
                <Upload className="w-4 h-4" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="match-csv-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Import Matches from CSV</DialogTitle>
                <DialogDescription>
                  Drop a schedule with columns: <code className="text-xs">home, away, date, time, venue, competition</code> (header optional).
                  Team names are fuzzy-matched against your existing teams; rows that don&apos;t match still import — you can fill in the Team ID later.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Upload Zone */}
                <div
                  className="upload-zone cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileSpreadsheet className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-foreground font-medium mb-1">
                    {csvFileName ? `Replace: ${csvFileName}` : "Drop your CSV file here"}
                  </p>
                  <p className="text-xs text-muted-foreground">or click to browse · {csvRows.length} rows loaded</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsvUpload}
                    className="hidden"
                    data-testid="match-csv-file-input"
                  />
                </div>

                {csvRows.length > 0 ? (
                  <>
                    {/* Match summary */}
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {(() => {
                        const matched = csvRows.filter(r => r.homeStatus === "matched" && r.awayStatus === "matched").length;
                        const partial = csvRows.filter(r => (r.homeStatus === "matched") !== (r.awayStatus === "matched")).length;
                        const unmatched = csvRows.length - matched - partial;
                        return (
                          <>
                            <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-center">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto mb-0.5" />
                              <span className="text-emerald-400 font-medium">{matched}</span> fully matched
                            </div>
                            <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20 text-center">
                              <AlertTriangle className="w-4 h-4 text-amber-500 mx-auto mb-0.5" />
                              <span className="text-amber-400 font-medium">{partial}</span> partial
                            </div>
                            <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-center">
                              <XCircle className="w-4 h-4 text-destructive mx-auto mb-0.5" />
                              <span className="text-destructive font-medium">{unmatched}</span> name not found
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Preview table */}
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-secondary sticky top-0">
                            <tr className="text-left">
                              <th className="px-2 py-2 font-medium">Home</th>
                              <th className="px-2 py-2 font-medium">Away</th>
                              <th className="px-2 py-2 font-medium">Date · Time</th>
                              <th className="px-2 py-2 font-medium">Venue</th>
                              <th className="px-2 py-2 font-medium">League</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvRows.slice(0, 200).map((r, i) => {
                              const cellClass = (status) =>
                                cn("px-2 py-1.5 truncate max-w-[120px]",
                                  status === "matched" && "text-emerald-400",
                                  status === "unmatched" && "text-destructive",
                                  status === "missing" && "text-muted-foreground italic",
                                );
                              return (
                                <tr key={i} className="border-t border-border">
                                  <td className={cellClass(r.homeStatus)} title={r.team1?.name || r.home}>
                                    {r.homeStatus === "matched" && "✓ "}
                                    {r.homeStatus === "unmatched" && "✗ "}
                                    {r.home || <span className="opacity-60">missing</span>}
                                  </td>
                                  <td className={cellClass(r.awayStatus)} title={r.team2?.name || r.away}>
                                    {r.awayStatus === "matched" && "✓ "}
                                    {r.awayStatus === "unmatched" && "✗ "}
                                    {r.away || <span className="opacity-60">missing</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-muted-foreground">
                                    {r.date}{r.time ? ` · ${r.time}` : ""}
                                  </td>
                                  <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[120px]" title={r.venue}>
                                    {r.venue || "—"}
                                  </td>
                                  <td className={cellClass(r.leagueStatus === "none" ? "missing" : r.leagueStatus)}
                                      title={r.league?.name || r.competition}>
                                    {r.leagueStatus === "matched" && "✓ "}
                                    {r.leagueStatus === "unmatched" && "✗ "}
                                    {r.competition || "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {csvRows.length > 200 && (
                        <div className="px-3 py-1.5 text-[11px] text-muted-foreground bg-secondary/50 border-t border-border">
                          Showing first 200 of {csvRows.length} rows. All will be imported.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                    <h4 className="text-xs font-medium text-foreground mb-1">Expected CSV columns</h4>
                    <code className="text-[11px] text-muted-foreground block">
                      home, away, date, time, venue, competition
                    </code>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Aliases supported: <code>team1/team2</code>, <code>home_team/away_team</code>,
                      <code>kickoff</code>, <code>league</code>, <code>tournament</code>.
                    </p>
                  </div>
                )}

                <Button variant="ghost" size="sm" className="gap-2" onClick={downloadSampleCSV}>
                  <Download className="w-4 h-4" />
                  Download sample CSV
                </Button>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setIsCsvDialogOpen(false)} disabled={csvImporting}>
                  Cancel
                </Button>
                <Button type="button" variant="premium" size="sm"
                  disabled={csvImporting || csvRows.length === 0}
                  onClick={handleConfirmImport}
                  data-testid="match-csv-confirm"
                >
                  {csvImporting ? "Importing..." : `Import ${csvRows.filter(r => r.home && r.away).length} match${csvRows.filter(r => r.home && r.away).length === 1 ? "" : "es"}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="premium" className="gap-2" onClick={openNewMatchDialog}>
                <Plus className="w-4 h-4" />
                Add Match
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editingMatch ? "Edit Match" : "Add New Match"}
                </DialogTitle>
                <DialogDescription>
                  Pick the home and away teams, the league, and an optional date,
                  time and venue.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Teams Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Team 1 *</Label>
                    {teams.length > 0 ? (
                      <Select
                        value={formData.team1Id || (formData.team1Name === "TBD" ? "tbd" : "")}
                        onValueChange={(value) => {
                          if (value === "tbd") {
                            setFormData((prev) => ({
                              ...prev,
                              team1Id: "",
                              team1Name: "TBD",
                            }));
                          } else {
                            const team = teams.find((t) => t.id === value);
                            setFormData((prev) => ({
                              ...prev,
                              team1Id: value,
                              team1Name: team?.name || prev.team1Name,
                            }));
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tbd">TBD (To Be Determined)</SelectItem>
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Team name or TBD"
                        value={formData.team1Name}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, team1Name: e.target.value }))
                        }
                        className="h-9"
                      />
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Team 2 *</Label>
                    {teams.length > 0 ? (
                      <Select
                        value={formData.team2Id || (formData.team2Name === "TBD" ? "tbd" : "")}
                        onValueChange={(value) => {
                          if (value === "tbd") {
                            setFormData((prev) => ({
                              ...prev,
                              team2Id: "",
                              team2Name: "TBD",
                            }));
                          } else {
                            const team = teams.find((t) => t.id === value);
                            setFormData((prev) => ({
                              ...prev,
                              team2Id: value,
                              team2Name: team?.name || prev.team2Name,
                            }));
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tbd">TBD (To Be Determined)</SelectItem>
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Team name or TBD"
                        value={formData.team2Name}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, team2Name: e.target.value }))
                        }
                        className="h-9"
                      />
                    )}
                  </div>
                </div>

                {/* League, Sport, Competition Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">League</Label>
                    <Select
                      value={formData.leagueId}
                      onValueChange={(value) => {
                        const league = leagues.find((l) => l.id === value);
                        setFormData((prev) => ({
                          ...prev,
                          leagueId: value,
                          competition: league?.name || prev.competition,
                          sport: league?.sport || prev.sport,
                        }));
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="League" />
                      </SelectTrigger>
                      <SelectContent>
                        {leagues.map((league) => (
                          <SelectItem key={league.id} value={league.id}>
                            {league.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Sport</Label>
                    <Select
                      value={formData.sport}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, sport: value }))
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORTS.map((sport) => (
                          <SelectItem key={sport} value={sport}>
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Competition</Label>
                    <Input
                      placeholder="e.g., Premier League"
                      value={formData.competition}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, competition: e.target.value }))
                      }
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Date, Time, Venue Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, date: e.target.value }))
                      }
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Time</Label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, time: e.target.value }))
                      }
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Venue</Label>
                    <Input
                      placeholder="Stadium"
                      value={formData.venue}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, venue: e.target.value }))
                      }
                      className="h-9"
                    />
                  </div>
                </div>

                <DialogFooter className="pt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={submitting} data-testid="match-submit-button">
                    {submitting ? "Saving..." : editingMatch ? "Save Changes" : "Add Match"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Bulk Actions */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search matches..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-secondary/50 border-border/50"
          />
        </div>
        {selectedMatchIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {selectedMatchIds.length} selected
            </span>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={handleDeleteSelected}
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </Button>
          </div>
        )}
      </div>

      {/* Matches Table */}
      {filteredMatches.length > 0 ? (
        <Card className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all"
                      className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                    />
                  </TableHead>
                  <TableHead>Match</TableHead>
                  <TableHead>League</TableHead>
                  <TableHead>Sport</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Thumbnail</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMatches.map((match) => {
                  const team1 = teams.find((t) => t.id === match.team1Id);
                  const team2 = teams.find((t) => t.id === match.team2Id);
                  const league = leagues.find((l) => l.id === match.leagueId);
                  const isSelected = selectedMatchIds.includes(match.id);

                  return (
                    <TableRow key={match.id} className={`border-border ${isSelected ? "bg-primary/5" : ""}`}>
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectMatch(match.id, checked)}
                          aria-label={`Select ${match.team1Name} vs ${match.team2Name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex -space-x-2">
                            <div className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center overflow-hidden">
                              {team1?.logo ? (
                                <img src={mediaUrl(team1.logo)} alt="" className="w-full h-full object-contain p-1"  loading="lazy" decoding="async"/>
                              ) : (
                                <span className="text-xs font-medium text-muted-foreground">
                                  {match.team1Name?.[0] || "T"}
                                </span>
                              )}
                            </div>
                            <div className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center overflow-hidden">
                              {team2?.logo ? (
                                <img src={mediaUrl(team2.logo)} alt="" className="w-full h-full object-contain p-1"  loading="lazy" decoding="async"/>
                              ) : (
                                <span className="text-xs font-medium text-muted-foreground">
                                  {match.team2Name?.[0] || "T"}
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {match.team1Name || "TBD"} vs {match.team2Name || "TBD"}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {league?.logo && (
                            <img src={mediaUrl(league.logo)} alt="" className="w-5 h-5 object-contain"  loading="lazy" decoding="async"/>
                          )}
                          <span className="text-sm text-muted-foreground">
                            {match.competition || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {match.sport && <span className="sport-badge">{match.sport}</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">
                            {match.date ? new Date(match.date).toLocaleDateString() : "-"}
                          </span>
                          {match.time && (
                            <span className="text-xs text-primary">{match.time}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {match.venue || "-"}
                      </TableCell>
                      <TableCell>
                        {match.thumbnailGenerated ? (
                          <CheckCircle2 className="w-5 h-5 text-success" />
                        ) : (
                          <span className="text-xs text-muted-foreground">Pending</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleEdit(match)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(match.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card className="glass-card">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No matches scheduled
            </h3>
            <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
              Import your match schedule via CSV or add matches manually.
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" className="gap-2" onClick={() => setIsCsvDialogOpen(true)}>
                <Upload className="w-4 h-4" />
                Import CSV
              </Button>
              <Button variant="premium" className="gap-2" onClick={openNewMatchDialog}>
                <Plus className="w-4 h-4" />
                Add Match
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
